import { ThreeJSContainer } from '../ui/webgl/ThreeJSContainer.js';
import { debounceCallback, roundNumber } from '../utils/utils.js';
import { WMSLayer } from './layer/WMSLayer.js';
import { Map } from './Map.js';

export { Map3D };

/**
 * 3D Map Implementation based on ThreeJS
 *
 * @author rhess <robin.hess@awi.de>
 * 
 * @memberof vef.map
 */
class Map3D extends Map {

    /** 
     * @param {string/HTMLElement} target id or HTMLElement
     * @param {Object} options options object
     */
    constructor(target, options) {
        super(target, options);

        // Only EPSG:4326 is supported
        this.options.crs = "EPSG:4326";

        this.getElement().classList.add("map-3d");

        this.threejs_ = this.initThreeJS_(this.mapContainer_);
        this.controls_ = this.initControls_(this.threejs_);
        this.globe_ = this.initGlobe_(this.threejs_.scene);
        this.wmsHelper_ = this.initWMSHelper_(this.threejs_, this.controls_, this.globe_);
        this.popup_ = this.initPopup_(this.threejs_, this.globe_);
        this.activeArea_ = null;

        if (this.options.ctrlToZoom) this.initCtrlToZoom_();
        this.initClickEvent_();
        this.setActiveArea("default-active-area", false);
    }

    initThreeJS_(target) {
        return new ThreeJSContainer(target, {
            css2d: true,
            cameraPosition: new THREE.Vector3(0, 0, 500),
            fov: 45,
            near: 1,
            far: 2500,
        });
    }

    initGlobe_(scene) {
        const globe = new ThreeGlobe();
        globe.showGlobe(false);

        scene.add(globe);
        scene.add(new THREE.AmbientLight(0xffffff));

        return globe;
    }

    initControls_(threejs) {
        const webglCanvas = threejs.getCanvas("webgl");
        const cssCanvas = threejs.getCanvas("css2d");
        cssCanvas.style.pointerEvents = "none";

        const controls = new THREE.TrackballControls(threejs.camera, webglCanvas);
        controls.minDistance = 102;
        controls.maxDistance = 1000;
        controls.rotateSpeed = 2.5
        controls.zoomSpeed = 0.5;
        controls.staticMoving = true;
        controls.noPan = true;

        let distance = 0;
        let center = new THREE.Vector3(0, 0, 0);
        threejs.addRenderCallback(() => {
            const newDistance = roundNumber(threejs.camera.position.distanceTo(center), 2);
            // ToDo: add zoomend events
            if (newDistance != distance) {
                distance = newDistance;
                this.closePopup();
            }
            controls.update()
        });
        threejs.on("resize", () => controls.handleResize());

        return controls;
    }

    initWMSHelper_(threejs, controls, globe) {
        const wmsHelper = new WMSHelper(globe);
        let resized = false;

        const update = () => {
            if (!resized) return;
            const layers = this.getLayers();
            const baseLayer = this.getBaseLayer();
            const bounds = this.getBounds();

            // add baselayer to the start of the list
            if (baseLayer) layers.unshift(baseLayer);

            wmsHelper.setLayers(layers);
            wmsHelper.update(bounds);
        }

        this.on("baselayer_change", update);
        this.on("add_layer", (layer) => {
            if (layer instanceof WMSLayer) {
                layer.getMapLayer("three-globe").on("redraw_wms", update);
                update();
            }
        });
        this.on("remove_layer", (layer) => {
            if (layer instanceof WMSLayer) {
                layer.getMapLayer("three-globe").off("redraw_wms");
                update();
            }
        });
        controls.addEventListener("end", debounceCallback(update, 500));

        threejs.on("resize", () => {
            if (resized) return;
            resized = true;
            update();
        });

        return wmsHelper;
    }

    initPopup_(threejs, globe) {
        const container = document.createElement("div")
        container.classList.add("vef-ui", "globe-popup");
        container.innerHTML = "<div class='popup-content'><i class='fas fa-times btn-close'></i><div class='popup-content-inner'></div></div>";

        const btnClose = container.querySelector(".btn-close");
        btnClose.addEventListener("click", () => this.closePopup());

        const popup = {
            active: false,
            container: container.querySelector(".popup-content-inner"),
            obj: new THREE.CSS2DObject(container)
        };

        threejs.addRenderCallback(() => {
            if (popup.active) {
                const globeDistance = threejs.camera.position.distanceTo(globe.position);
                const popupDistance = threejs.camera.position.distanceTo(popup.obj.position);
                if (globeDistance < popupDistance) this.closePopup();
            }
        });

        this.on("add_layer", () => this.closePopup());
        this.on("remove_layer", () => this.closePopup());

        return popup;
    }

    /**
     * @private
     */
    initCtrlToZoom_() {
        const element = this.getElement();
        let resetScrollTimeout = null;

        this.controls_.noZoom = true;

        const eventCallback = (event) => {
            event.stopPropagation();
            if (event.ctrlKey == true) {
                event.preventDefault();
                this.controls_.noZoom = false;
                element.classList.remove('ctrl-To-zoom');
            } else {
                this.controls_.noZoom = true;
                element.classList.add('ctrl-To-zoom');
            }
            clearTimeout(resetScrollTimeout);
            resetScrollTimeout = setTimeout(() => {
                this.controls_.noZoom = true;
                element.classList.remove('ctrl-To-zoom');
            }, 2000);
        }

        element.addEventListener('mousewheel', eventCallback, false);
        element.addEventListener('DOMMouseScroll', eventCallback, false);
    }

    showPopup(lat, lng, content) {
        this.closePopup();

        this.popup_.container.innerHTML = "";
        this.popup_.container.appendChild(content);

        let point = this.globe_.getCoords(lat, lng, 0);
        point = new THREE.Vector3(point.x, point.y, point.z);
        this.popup_.obj.position.copy(point);

        this.threejs_.scene.add(this.popup_.obj);
        this.popup_.active = true;
    }

    closePopup() {
        this.popup_.active = false;
        this.popup_.obj.removeFromParent();
    }

    raycastGlobe_(point) {
        const intersections = this.threejs_.raycast(point, this.globe_);
        for (let i = 0; i < intersections.length; ++i) {
            if (intersections[i].object.__globeObjType == "globe") {
                return intersections[i];
            }
        }
        return null;
    }

    initClickEvent_() {
        this.threejs_.on("click", (e) => {
            const intersection = this.raycastGlobe_(e.normalizedPoint);
            if (intersection) {
                this.fire("click", this.globe_.toGeoCoords(intersection.point));
            }
        });
    }

    addLayer(layer) {
        if (layer instanceof WMSLayer) {
            layer.getMapLayer("three-globe"); // needs to be created first
            layer.enable("three-globe");
            layer.setProjection(this.options.crs);
            this.layers_.push(layer);
            this.fire("add_layer", layer);
        }
    }

    removeLayer(layer) {
        const index = this.layers_.indexOf(layer);
        if (index >= 0) {
            this.layers_.splice(index, 1);
            layer.disable();
            this.fire("remove_layer", layer);
        }
    }

    setBaseLayer(layer, crs) {
        if (layer) {
            if (this.baseLayer_) this.baseLayer_.disable();

            layer.getMapLayer("three-globe");
            layer.enable("three-globe");
            layer.setProjection(this.options.crs);
            this.baseLayer_ = layer;
            this.options.baseLayer = this.baseLayer_.title;

            this.fire("baselayer_change", layer);
        }
    }

    /**
     * sets the active area div
     * 
     * @param {string} className css class name
     * @param {boolean} centerMap automatically re-center the map (default = false)
     */
    setActiveArea(className, centerMap) {
        if (this.activeArea_) {
            this.toolOverlay_.classList.remove(this.activeArea_);
            this.mapContainer_.classList.remove(this.activeArea_);
        }

        this.activeArea_ = className;
        this.toolOverlay_.classList.add(className);
        this.mapContainer_.classList.add(className);
    }

    getCenter() {
        const center = new THREE.Vector2(0, 0);
        const intersection = this.raycastGlobe_(center);
        return (intersection) ? this.globe_.toGeoCoords(intersection.point) : null;
    }

    /**
     * Get the current bounds as cartesian spatial coordinates
     *  
     * @returns {object} 
     */
    getBounds() {
        const bounds = {
            min: { x: null, y: null },
            max: { x: null, y: null },
            crs: "CRS:84"
        };

        const center = this.getCenter();
        let edges = 0;
        let latOverflow = false;

        const step = 10;
        const radius = 100; // large integer to avoid float rounding issues

        for (let x = -radius; x <= radius; x += step) {
            for (let y = -radius; y <= radius; y += step) {
                if ((Math.abs(x) != radius) && (Math.abs(y) != radius)) continue;

                const intersection = this.raycastGlobe_(new THREE.Vector2(x / radius, y / radius));
                if (!intersection) continue;

                const point = this.globe_.toGeoCoords(intersection.point);
                const delta = [
                    Math.min(Math.abs(center.lng - point.lng), Math.abs(center.lng - point.lng + 360)),
                    Math.min(Math.abs(point.lng - center.lng), Math.abs(point.lng - center.lng + 360))
                ];

                if (!latOverflow) {
                    if (Math.min(delta[0], delta[1]) > 90) {
                        latOverflow = true;
                        point.lat = (center.lat > 0) ? 90 : -90;
                        bounds.min.x = -180;
                        bounds.max.x = 180;
                    } else if (delta[0] > delta[1]) {
                        point.lng += 360;
                    } else if (delta[0] < delta[1]) {
                        point.lng -= 360;
                    }

                    if (!Number.isFinite(bounds.min.x) || point.lng < bounds.min.x) bounds.min.x = point.lng;
                    if (!Number.isFinite(bounds.max.x) || point.lng > bounds.max.x) bounds.max.x = point.lng;
                }

                if (!Number.isFinite(bounds.min.y) || point.lat < bounds.min.y) bounds.min.y = point.lat;
                if (!Number.isFinite(bounds.max.y) || point.lat > bounds.max.y) bounds.max.y = point.lat;

                if ((y == 0) || (x == 0)) ++edges;
            }
        }

        const isSet = val => (!Number.isFinite(val) || (edges < 4));

        if (isSet(bounds.min.x)) bounds.min.x = -180;
        if (isSet(bounds.max.x)) bounds.max.x = 180;
        if (isSet(bounds.min.y)) bounds.min.y = ((center.lat - 90) > -90) ? (center.lat - 90) : -90;
        if (isSet(bounds.max.y)) bounds.max.y = ((center.lat + 90) < 90) ? (center.lat + 90) : 90;

        // only let longitude overflow in the positive direction
        if (bounds.min.x < -180) {
            bounds.min.x += 360;
            bounds.max.x += 360;
        }

        return bounds;
    }

    /**
      * Build a bounding box from a click location
      * for getFeatureInfo requests
      * 
      * @param {number} lat click lat
      * @param {number} lng click lng
      */
    getFeatureInfoOptions(lat, lng) {
        // find tile that contains the clicked point
        const min = { x: -180, y: -90 };
        while ((min.x + this.wmsHelper_.tileWidth) <= lng) min.x += this.wmsHelper_.tileWidth;
        while ((min.y + this.wmsHelper_.tileHeight) <= lat) min.y += this.wmsHelper_.tileHeight;

        const max = {
            x: min.x + this.wmsHelper_.tileWidth,
            y: min.y + this.wmsHelper_.tileHeight,
        };

        const size = this.wmsHelper_.imageResolution;
        const x = ((lng - min.x) / (max.x - min.x)) * size;
        const y = ((max.y - lat) / (max.y - min.y)) * size;

        return {
            container: {
                width: size,
                height: size,
                x: Math.round(x),
                y: Math.round(y),
            },
            click: {
                x: lng,
                y: lat
            },
            crs: this.options.crs,
            min: min,
            max: max
        };
    }

    /**
     * Idea: possibility to suspend and re-enable a map instance renderer
     */
    suspend() { }

}

/**
 * Internal helper class to render and manage WMS Layers
 * Currently only supports EPSG:4326 for requests
 * 
 * @private
 */
class WMSHelper {

    constructor(globe) {
        this.globe = globe;
        this.layers = [];

        // use factors of 180 to always have an eaven grid
        this.tileSizes = [0.5, 1, 2, 3, 4, 5, 6, 9, 10, 12, 15, 18, 20, 30, 36, 45, 60, 90, 180];
        this.maxGridSize = [6, 5];
        this.imageResolution = 256;

        // only "EPSG:4326" is supported
        this.crs = "EPSG:4326";

        this.imageCache = {};
        this.materials = [];
        this.loadingImages = [];

        // current states
        this.initialized = false;
        this.tileHeight = 90;
        this.tileWidth = 180;
    }

    reset() {
        for (let i = 0; i < this.loadingImages.length; ++i) {
            if (!this.loadingImages[i].complete) {
                this.loadingImages[i].onload = null;
                this.loadingImages[i].src = "";
            }
        }

        for (let i = 0; i < this.materials.length; ++i) {
            this.materials[i].map.dispose();
            this.materials[i].dispose();
        }

        this.materials = [];
        this.loadingImages = [];
    }

    getImage(lng, lat, width, height, url, callback) {
        if (!(lng in this.imageCache)) this.imageCache[lng] = {};
        if (!(lat in this.imageCache[lng])) this.imageCache[lng][lat] = {};
        if (!(width in this.imageCache[lng][lat])) this.imageCache[lng][lat][width] = {};
        if (!(height in this.imageCache[lng][lat][width])) this.imageCache[lng][lat][width][height] = {};

        let img = null;

        if (url in this.imageCache[lng][lat][width][height]) {
            img = this.imageCache[lng][lat][width][height][url];
            img.onload = null;
            setTimeout(() => callback(img), 0);
        } else {
            img = new Image();
            img.onload = () => {
                img.onload = null;
                this.imageCache[lng][lat][width][height][url] = img;
                callback(img);
            }
            img.src = url;

            this.loadingImages.push(img);
        }

        return img;
    }

    getTile(lng, lat, width, height, index) {
        const bbox = this.createBbox(lng, lat, width, height);
        const canvas = document.createElement('canvas');
        const context = canvas.getContext("2d");
        const texture = new THREE.CanvasTexture(canvas);
        const material = new THREE.MeshLambertMaterial({ color: "#ffffff", map: texture, transparent: true });

        canvas.width = this.imageResolution;
        canvas.height = this.imageResolution;
        texture.anisotropy = 4;

        const tileImages = [];
        for (let i = 0; i < this.layers.length; ++i) {
            const l = this.layers[i];
            if (l instanceof WMSLayer) {

                const url = l.serviceUrl + this.getURL_(l, bbox);
                const img = this.getImage(lng, lat, width, height, url, () => {
                    context.clearRect(0, 0, this.imageResolution, this.imageResolution);
                    tileImages.forEach(image => {
                        if (image.complete) {
                            context.drawImage(image, 0, 0);
                            texture.needsUpdate = true;
                            material.needsUpdate = true;
                        }
                    });
                });

                tileImages.push(img);
            }
        }

        this.materials.push(material);
        return material;
    }

    setLayers(layers) {
        this.layers = layers;
        this.sortLayers();
    }

    sortLayers() {
        this.layers.sort((a, b) => {
            if (!(a instanceof WMSLayer)) return 1;
            if (!(b instanceof WMSLayer)) return -1;
            if ((a.zIndex_ > b.zIndex_)) return 1;
            if ((a.zIndex_ < b.zIndex_)) return -1;

            return 0;
        });
    }

    createBbox(lng, lat, width, height) {
        const p1 = [lat, lng];
        const p2 = [lat + height, lng + width];

        const min = [
            (p1[0] < p2[0]) ? p1[0] : p2[0],
            (p1[1] < p2[1]) ? p1[1] : p2[1],
        ]

        const max = [
            (p1[0] >= p2[0]) ? p1[0] : p2[0],
            (p1[1] >= p2[1]) ? p1[1] : p2[1],
        ]

        return [min[0], min[1], max[0], max[1]];
    }

    update(bounds) {
        this.reset();

        const width = this.getTileSize_((bounds.max.x - bounds.min.x) / this.maxGridSize[0], this.tileSizes);
        const height = this.getTileSize_((bounds.max.y - bounds.min.y) / this.maxGridSize[1], this.tileSizes);

        const start = {
            lng: this.getStartDegree_(bounds.min.x, width, -180),
            lat: this.getStartDegree_(bounds.min.y, width, -90)
        }

        const tilesData = [];

        let index = 0;
        for (let lng = start.lng; lng <= bounds.max.x; lng += width) {
            for (let lat = start.lat; lat <= bounds.max.y; lat += height) {
                const normalizedLng = (lng >= 180) ? (lng - 360) : lng;
                const material = this.getTile(normalizedLng, lat, width, height, index++)

                tilesData.push({
                    lng: normalizedLng + (0.5 * width),
                    lat: lat + (0.5 * height),
                    material: material
                });
            }
        }

        this.tileWidth = width;
        this.tileHeight = height;
        this.globe
            .tilesData(tilesData)
            .tileWidth(width)
            .tileHeight(height)
            .tileMaterial('material')
            .tileAltitude(0)
            .tileCurvatureResolution(0.5)
            .tilesTransitionDuration(0);
    }

    getURL_(layer, bbox) {
        const params = Object.assign({
            version: layer.serviceVersion,
            crs: this.crs,
            width: this.imageResolution,
            height: this.imageResolution,
            layers: layer.getRequestName(),
            bbox: (layer.serviceVersion == "1.3.0") ? bbox : [bbox[1], bbox[0], bbox[3], bbox[2]],
        }, layer.currentQueryParameters_);

        let url = "?service=WMS&request=GetMap&transparent=true&format=image/png&continuousWorld=true";
        for (let key in params) {
            url += "&" + key + "=" + encodeURIComponent(params[key])
        }
        return url;
    }

    /**
     * Returns the closest allowed tilesize based on the original tile size
     * 
     * @param {nmber} num original tile size
     * @param {number[]} arr sorted from high to low !
     */
    getTileSize_(num, arr) {
        let result = arr[0];
        for (let i = 0; i < arr.length; ++i) {
            let currentDelta = (num > result) ? (num - result) : (result - num);
            let nextDelta = (num > arr[i]) ? (num - arr[i]) : (arr[i] - num);

            if (currentDelta < nextDelta) break;
            result = arr[i]
        }
        return result;
    }

    getStartDegree_(originalStart, tileSize, min) {
        let start = min;

        while ((start + tileSize) < originalStart) {
            start += tileSize;
        }

        return start
    }

}